import { useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StoreState } from "../models/states/StoreState";
import { DefaultFocusStatusModel } from "../models/DefaultFocusStatusModel";
import { setDefaultFocusStatus } from "../redux/actions/index";

export function useKeyboardNavigationForLookupTable<E extends React.SyntheticEvent<any>>(
    questionId,
    elementId,
    variable,
    question,
    NavigationClass,
    onSelect?: React.EventHandler<E> | null,
    lookupTableGroupType = null,
) {
    const elementReference = useRef(null);
    const defaultFocusStatus = useSelector((state: StoreState) => state.defaultFocusStatus);
    const dispatch = useDispatch();

    const navigation = new NavigationClass(defaultFocusStatus, dispatch, question);

    function getCurrentElementFromStore(): DefaultFocusStatusModel | undefined {
        return defaultFocusStatus.statuses.find(
            (el) => el.variableId === variable.id && el.elementId === elementId,
        );
    }

    function getPageNumber(elementId: string): number {
        const pageNumber = parseInt(elementId.split('-')[elementId.split('-').length - 1], 10);
        return isNaN(pageNumber) ? -1 : pageNumber;
    }

    useEffect(() => {
        if (variable && defaultFocusStatus && defaultFocusStatus.statuses) {
            const focusStatus = defaultFocusStatus.statuses.find((element) => element.focusStatus);
            const currentElementStatus = getCurrentElementFromStore();
            const newElementStatus = {
                elementId,
                elementRef: elementReference,
                variableId: variable.id,
                focusStatus: false,
                lookupTableGroupType: lookupTableGroupType,
                questionId: question ? question.id : null,
            }
            const lookupTableLastRowIndex = defaultFocusStatus.statuses.reduce((lastIndex, element, index) => {
                if (element.lookupTableGroupType === "lookupTableRow") {
                    return index;
                }
                return lastIndex;
            }, -1);
            const lookupTableLastElementIndex = defaultFocusStatus.statuses.reduce((lastIndex, element, index) => {
                if (element.questionId === questionId) {
                    return index;
                }
                return lastIndex;
            }
                , -1);
            if (!currentElementStatus) {
                if (lookupTableLastRowIndex > -1 && lookupTableGroupType === "lookupTableRow") {
                    defaultFocusStatus.statuses.splice(lookupTableLastRowIndex + 1, 0, newElementStatus);
                } else if (lookupTableGroupType === "groupButtonPagination") {
                    const currentPageNumber = getPageNumber(elementId);
                    if (currentPageNumber < 2) {
                        defaultFocusStatus.statuses.splice(lookupTableLastElementIndex + 1, 0, newElementStatus);
                    } else {
                        const closestPagination = defaultFocusStatus.statuses.reduce((closest, element, index) => {
                            if (element.lookupTableGroupType !== "groupButtonPagination" && element.questionId != questionId) return closest;

                            const pageNumber = getPageNumber(element.elementId);
                            if (pageNumber === currentPageNumber) return closest;

                            const diff = Math.abs(pageNumber - currentPageNumber);
                            return diff < closest.difference
                                ? { index, side: pageNumber > currentPageNumber ? 'left' : 'right', difference: diff }
                                : closest;
                        }, { index: -1, side: null, difference: Infinity });

                        const insertIndex = closestPagination.index === -1
                            ? lookupTableLastElementIndex + 1
                            : closestPagination.side === 'left'
                                ? closestPagination.index
                                : closestPagination.index + 1;

                        defaultFocusStatus.statuses.splice(insertIndex, 0, newElementStatus);
                    }
                }
                else {
                    defaultFocusStatus.statuses.splice(lookupTableLastElementIndex + 1, 0, newElementStatus);
                }
                dispatch(setDefaultFocusStatus(defaultFocusStatus));
            } else if (!focusStatus && currentElementStatus) {
                const index = defaultFocusStatus.statuses.indexOf(currentElementStatus);
                defaultFocusStatus.statuses[index].focusStatus = false;
                dispatch(setDefaultFocusStatus(defaultFocusStatus));
            } else if (currentElementStatus) {
                let index = defaultFocusStatus.statuses.indexOf(currentElementStatus);
                if (index > -1) {
                    defaultFocusStatus.statuses[index].elementRef.current = document.getElementById(elementId);
                    dispatch(setDefaultFocusStatus(defaultFocusStatus));
                }
            }
        }
    }, []);

    function onKeyDown(
        event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement | HTMLButtonElement>,
    ) {
        if (event.target !== event.currentTarget) return;

        if (event.key === " " && onSelect) {
            onSelect(event);
            event.preventDefault();

            // Set the focus to current element even in case of element selection as well
            const currentElementInfo = getCurrentElementFromStore();
            const index = defaultFocusStatus.statuses.indexOf(currentElementInfo);
            defaultFocusStatus.statuses[index].focusStatus = true;
            dispatch(setDefaultFocusStatus(defaultFocusStatus));
        } else {
            const currentElementInfo = getCurrentElementFromStore();
            if (currentElementInfo) {
                navigation.handleKeyPressEvent(event, currentElementInfo);
            }
        }
    }

    return { elementReference, onKeyDown };
}
